import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { useDemoMode } from '../components/demo/DemoMode';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString('hi-IN', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return '—'; } };

export default function BillingMyPage({ token }) {
  const { language } = useLanguage();
  const L = (en, hi) => (language === 'hi' ? hi : en);
  const STATE_LABEL = {
    paid: { t: L('✅ Active (Paid)', '✅ सक्रिय (भुगतान)'), c: 'bg-green-100 text-green-700' },
    trial: { t: L('🆓 Trial', '🆓 ट्रायल'), c: 'bg-blue-100 text-blue-700' },
    demo: { t: L('🎬 Demo', '🎬 डेमो'), c: 'bg-amber-100 text-amber-700' },
  };
  const authToken = token || localStorage.getItem('token');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const demo = useDemoMode();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/billing/my`, { headers: { Authorization: `Bearer ${authToken}` } });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail);
      setData(d);
    } catch (e) { toast.error(L('Failed to load billing', 'Billing load नहीं हुआ')); }
    finally { setLoading(false); }
  }, [authToken]);
  useEffect(() => { load(); }, [load]);

  const payNow = () => {
    if (demo?.openActivateModal) demo.openActivateModal();
    else window.location.href = '/activate';
  };

  const downloadReceipt = async (pid) => {
    try {
      const res = await fetch(`${API_URL}/api/billing/my/receipt/${pid}`, { headers: { Authorization: `Bearer ${authToken}` } });
      if (!res.ok) { toast.error(L('Receipt not found', 'Receipt नहीं मिला')); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `receipt_${pid.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error(L('Download error', 'Download error')); }
  };

  if (loading) return <div className="p-6 text-center text-slate-400" data-testid="billing-my-loading">⏳ {L('Loading...', 'लोड हो रहा है...')}</div>;
  if (!data) return null;
  const b = data.billing;
  const st = STATE_LABEL[b.account_state] || { t: b.account_state, c: 'bg-slate-100' };

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-5" data-testid="billing-my-page">
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-5 text-white">
        <h1 className="text-xl font-bold">💳 {L('My Billing', 'मेरा Billing')}</h1>
        <p className="text-sm text-white/90 mt-1">{b.school_name}</p>
        <span className={`inline-block mt-2 text-xs px-2.5 py-1 rounded-full ${st.c}`}>{st.t}</span>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-4 text-center"><p className="text-xl font-bold text-slate-800">{b.total_users}</p><p className="text-[11px] text-slate-500">{L('Total Users', 'कुल Users')}</p></div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 text-center"><p className="text-lg font-bold text-slate-800">{b.is_custom ? L('Custom', 'Custom') : inr(b.one_time_price)}</p><p className="text-[11px] text-slate-500">{L('One-time', 'एक-बार')}</p></div>
        <div className="bg-emerald-50 rounded-xl p-4 text-center"><p className="text-lg font-bold text-emerald-700">{b.is_custom ? '—' : inr(b.annual_maintenance)}</p><p className="text-[11px] text-slate-500">{L('Yearly Maintenance', 'सालाना Maintenance')}</p></div>
        <div className="bg-rose-50 rounded-xl p-4 text-center"><p className="text-lg font-bold text-rose-700">{b.is_custom ? L('Custom', 'Custom') : inr(b.amount_due)}</p><p className="text-[11px] text-slate-500">{L('Amount Due', 'बकाया राशि')}</p></div>
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-600" data-testid="pricing-note">
        {b.is_custom
          ? L(`5000+ users — custom pricing. Contact sales: ${b.sales_contact_phone}`, `5000+ users — custom pricing। Sales से बात करें: ${b.sales_contact_phone}`)
          : L(`Software is FREE. Your slab (${b.tier_label}): ${inr(b.one_time_price)} one-time + ${inr(b.annual_maintenance)}/year maintenance. Price is by total users (students + teachers + staff) — no per-user charge.`, `Software FREE है। आपका slab (${b.tier_label}): ${inr(b.one_time_price)} one-time + ${inr(b.annual_maintenance)}/साल maintenance। कीमत कुल users (छात्र + शिक्षक + staff) पर — कोई per-user शुल्क नहीं।`)}
      </div>

      {b.account_state !== 'paid' && (
        <button onClick={payNow} className="w-full py-3 rounded-xl bg-green-600 text-white font-bold hover:bg-green-700" data-testid="pay-now-btn">
          🚀 {L(`Pay Now (${inr(b.amount_due)}) — get full access`, `अभी Pay करें (${inr(b.amount_due)}) — पूरा access पाएँ`)}
        </button>
      )}

      {/* History */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <p className="text-sm font-bold text-slate-700 mb-3">📜 {L('Payment History', 'भुगतान इतिहास')}</p>
        {(!data.history || data.history.length === 0) ? (
          <p className="text-xs text-slate-400 text-center py-4" data-testid="my-no-history">{L('No payments yet.', 'अभी तक कोई payment नहीं हुआ।')}</p>
        ) : (
          <div className="space-y-2" data-testid="my-history">
            {data.history.map((h, i) => (
              <div key={i} className="flex justify-between items-center border border-slate-100 rounded-lg p-3 text-sm">
                <div>
                  <span className="font-semibold text-slate-800">{inr(h.amount_inr)}</span>
                  <span className="text-xs text-slate-400 ml-2">{h.method || h.payment_type}</span>
                </div>
                <div className="text-right">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${['success', 'paid', 'captured'].includes(h.status) ? 'bg-green-50 text-green-600' : 'bg-slate-100 text-slate-500'}`}>{h.status}</span>
                  <p className="text-[10px] text-slate-400 mt-0.5">{fmtDate(h.created_at)}</p>
                  {['success', 'paid', 'captured'].includes(h.status) && (
                    <button onClick={() => downloadReceipt(h.id)} className="mt-1 text-[10px] px-2 py-0.5 rounded bg-slate-700 text-white" data-testid={`my-receipt-${h.id}`}>📄 {L('Receipt', 'रसीद')}</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
